import { useState, useEffect, useContext, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, TextField, Button, Icon, IconButton, Tooltip, Switch } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import DateRange from '../../common/date-range';
import TicketMasterToPrintSummary from '../components/ticket-master-to-print-summary';
import TicketMasterToPrintDetail from '../components/ticket-master-to-print-detail';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';
import themeColors from '../../assets/colors';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginBottom: 20
    },
    searchBtn: {
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    printBtn: {
        marginRight: 20,
        backgroundColor: '#6E6E6E',
        borderColor: '#6E6E6E',
        '&:hover': {
            backgroundColor: '#6E6E6E',
            borderColor: '#6E6E6E',
            boxShadow: 'none'
        }
    },
    tableContainer: {
        width: '100%',
        marginBottom: 20,
        '& .row-error': {
            backgroundColor: themeColors.visible['false']
        },
    },
    dataToPrint: {
        display: 'none'
    }
}));

const defSupplier = { id: 0, name: 'All suppliers' };
const defLand = { id: 0, name: 'All lands' };
const defLocation = { id: 0, name: 'All locations' };

const TicketMasterIndex = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [selectedData, setSelectedData] = useState([]);
    const [summaryToPrint, setSummaryToPrint] = useState([]);
    const [detailToPrint, setDetailToPrint] = useState([]);
    const [scaleSwitch, setScaleSwitch] = useState(true);
    const [state, setState] = useState({
        loading: true,
        suppliers: [],
        lands: [],
        locations: [],
        filter: {
            code: '',
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            supplier: defSupplier,
            land: defLand,
            location: defLocation
        },
        data: [],
        dataToPrint: [],
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    const summaryPrintRef = useRef(null);
    const detailPrintRef = useRef(null);

    const handleSummaryPrint = useReactToPrint({
        content: () => summaryPrintRef.current
    });
    const handleDetailPrint = useReactToPrint({
        content: () => detailPrintRef.current
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Ticket master';

        makeRequest();
    }, []);

    useEffect(() => {
        if (summaryToPrint.length > 0) {
            handleSummaryPrint();
        }
    }, [summaryToPrint]);

    useEffect(() => {
        if (detailToPrint.length > 0) {
            handleDetailPrint();
        }
    }, [detailToPrint]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketMaster', state.filter);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response?.data ?? [],
                suppliers: response?.suppliers ?? [],
                lands: response?.lands ?? [],
                locations: response?.locations ?? []
            }));
        } else {
            console.log('[TicketMasterIndex] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleChange = ({ target: { name, value } }) => setState(prev => ({ ...prev, filter: { ...prev.filter, [name]: value } }));

    const handleChangeAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const search = async () => {
        showLoader(true);
        const response = await get('/ticketMaster/search', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response?.data ?? [] }));

            if (!response?.data[0]) showToast('There are no master tickets with these filters', 'warning');
        } else {
            console.log('[TicketMasterIndex] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const getDataToPrint = async type => {
        const ids = selectedData;

        if (ids.length < 1) {
            showToast('Select some data', 'error');
            return null;
        }

        showLoader(true);
        const response = await get(`/ticketMaster/print${type}`, { idticket_master: ids.join('-') });
        if (response.status === 200) {
            if (type === 'Summary') setSummaryToPrint(response?.data ?? []);
            if (type === 'Detail') setDetailToPrint(response?.data ?? []);

            if (response.data?.length === 0) showToast('There is no data to print', 'warning');
        } else {
            console.log('[TicketMasterIndex] getDataToPrint error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const getTotal = ({ value }) => `$ ${value}`;
    const getDate = ({ value }) => dayjs(value).format('MM/DD/YYYY');
    const getNegative = ({row})=> row.net_weight_software - row.total_qty;
    const valueGetter = params => params.value.name;
    const renderCell = ({ row }) => {

        return (
            <Tooltip title='Show detail' placement='right'>
                <Link to={`/ticket/tickets/master/${row.idticket_master}`} target='_blank'>
                    <IconButton>
                        <Icon>{'visibility'}</Icon>
                    </IconButton>
                </Link>
            </Tooltip>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 0.7, minWidth: 112 },
        { field: 'total', headerName: 'Total', flex: 0.7, minWidth: 112, valueGetter: getTotal },
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 160, valueGetter: getDate },
        { field: 'supplier', headerName: 'Supplier', minWidth: 160, flex: 1, valueGetter },
        { field: 'land', headerName: 'Land', flex: 1, minWidth: 160, valueGetter },
        { field: 'location', headerName: 'Location', flex: 1, minWidth: 160, valueGetter },
        { field: 'detail', headerName: 'Detail', flex: 0.5, minWidth: 80, renderCell }
    ]

    const columnsScale = [
        { field: 'code', headerName: 'Code', flex: 0.7, minWidth: 112 },
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 160, valueGetter: getDate },
        { field: 'total', headerName: 'Total', flex: 0.7, minWidth: 112, valueGetter: getTotal },
        { field: 'net_weight_scale', headerName: 'Net weight scale', flex: 0.7, minWidth: 112 },
        { field: 'net_weight_software', headerName: 'Net weight software', flex: 0.7, minWidth: 112 },
        { field: 'total_qty', headerName: 'Total qty', flex: 0.7, minWidth: 112 },
        { field: 'negative', headerName: '(Negative)', flex: 0.7, minWidth: 112, valueGetter: getNegative },
        { field: 'supplier', headerName: 'Supplier', minWidth: 160, flex: 1, valueGetter },
        { field: 'land', headerName: 'Land', flex: 1, minWidth: 160, valueGetter },
        { field: 'location', headerName: 'Location', flex: 1, minWidth: 160, valueGetter },
        { field: 'detail', headerName: 'Detail', flex: 0.5, minWidth: 80, renderCell }
    ]

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <h2>{'Master'}</h2>
            <Grid container>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Code'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Code'
                                name='code'
                                onChange={handleChange}
                                value={state.filter.code}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                autoComplete='off'
                                type='text'
                                placeholder='Date range'
                                name='range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.suppliers}
                            value={state.filter.supplier}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('supplier', value)}
                            renderInput={params => <TextField {...params} placeholder='Supplier' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Land'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.lands}
                            value={state.filter.land}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('land', value)}
                            renderInput={params => <TextField {...params} placeholder='Land' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={4}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.locations}
                            value={state.filter.location}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('location', value)}
                            renderInput={params => <TextField {...params} placeholder='Location' />}
                        />
                    </FormGroup>
                </Grid>

                <Grid item xs={6} lg={4}>
                    <FormGroup style={{ alignItems: 'center' }}>
                        <FormLabel>{'Scale tickets'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                            style={{ alignItems: 'center' }}
                        >
                            <Switch
                                name='is_automatic'
                                onChange={({ target }) => setScaleSwitch(target.checked)}
                                checked={scaleSwitch}
                            />
                            <FormLabel>{scaleSwitch ? 'Yes' : 'No'}</FormLabel>
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.buttonsContainer}>
                <Button
                    variant='contained'
                    className={classes.printBtn}
                    onClick={() => getDataToPrint('Detail')}
                >
                    {'Print detail'}
                </Button>
                <Button
                    variant='contained'
                    className={classes.printBtn}
                    onClick={() => getDataToPrint('Summary')}
                >
                    {'Print summary'}
                </Button>
                <Button
                    variant='contained'
                    className={classes.searchBtn}
                    onClick={search}
                >
                    {'Search'}
                </Button>
            </div>

            <div className={classes.tableContainer}>
                {
                    scaleSwitch ?
                        <DataGrid
                            autoHeight
                            pageSize={45}
                            density='compact'
                            columns={columnsScale}
                            rows={state.data.filter(item => item.is_automatic === false)}
                            getRowId={row => row.idticket_master}
                            getRowClassName={({ row }) => (row.error) ? 'row-error' : ''}
                            checkboxSelection
                            disableSelectionOnClick
                            onSelectionModelChange={array => setSelectedData(array)}
                        />
                        :
                        <DataGrid
                            autoHeight
                            pageSize={45}
                            density='compact'
                            columns={columns}
                            rows={state.data.filter(item => item.is_automatic === true)}
                            getRowId={row => row.idticket_master}
                            getRowClassName={({ row }) => (row.error) ? 'row-error' : ''}
                            checkboxSelection
                            disableSelectionOnClick
                            onSelectionModelChange={array => setSelectedData(array)}
                        />
                }
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />

            <div className={classes.dataToPrint}>
                <div ref={summaryPrintRef}>
                    <TicketMasterToPrintSummary data={summaryToPrint} />
                </div>
            </div>

            <div className={classes.dataToPrint}>
                <div ref={detailPrintRef}>
                    <TicketMasterToPrintDetail data={detailToPrint} />
                </div>
            </div>
        </div>

    );
}

export default TicketMasterIndex;